<template>
  <div class="portfolio-page">
    <!-- Kopfbereich im Look & Feel deiner LandingPage -->
    <header class="portfolio-hero">
      <div class="container hero-inner">
        <div class="hero-text">
          <h1 class="title">Referenzprojekte</h1>
          <p class="subtitle">Erfolgreiche Digitalisierung für zufriedene Geschäftskunden</p>
        </div>
        
        <div class="controls">
            
          <button @click="goBackOrLogout" class="back-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 3h6v18h-6M10 17l-5-5 5-5M19 12H5"/>
            </svg>
            <span>Logout</span> 
          </button>  
            
          <input
            v-model="query"
            type="search"
            class="search"
            placeholder="Projekte durchsuchen…"
            aria-label="Projekte durchsuchen"
          />
        </div>
      </div>
    </header>

    <main class="container section">
      <!-- Ladezustand -->
      <div v-if="loading" class="grid">
        <div v-for="i in 6" :key="i" class="card skeleton"></div>
      </div>

      <!-- Fehlerzustand -->
      <div v-else-if="error" class="notice error">
        <strong>Fehler</strong>
        <p>{{ error }}</p>
      </div>

      <!-- Inhalt -->
      <div v-else>
        <div v-if="filtered.length" class="grid">
          <article
            v-for="p in filtered"
            :key="p.id"
            class="card"
            @click="openProject(p)"
            :class="{ disabled: !p.has_build }"
          >
            <div
              class="media"
              :style="!p.thumbnail_url ? gradientStyle(p) : {}"
            >
              <img v-if="p.thumbnail_url" :src="p.thumbnail_url" :alt="p.name" />
              <div class="overlay" v-if="p.has_build">
                <button class="view-btn" type="button">Website öffnen</button>
              </div>
              <div class="device-icon" aria-hidden="true">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
            </div>

            <div class="info">
              <div class="meta">
                <span class="category">{{ p.category || 'Projekt' }}</span>
                <span class="year">{{ p.year || new Date(p.updated_at || Date.now()).getFullYear() }}</span>
              </div>
              <h3 class="name">{{ p.name }}</h3>
              <p class="desc">{{ p.description }}</p>

              <div class="tags" v-if="p.tags?.length">
                <span class="tag" v-for="t in p.tags" :key="t">{{ t }}</span>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="notice">
          <strong>Keine Projekte gefunden</strong>
          <p>Prüfe die Suche oder füge neue Projekte hinzu.</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { fetchProjects, logout } from '@/apiService' 

const router = useRouter()
const loading = ref(true)
const error = ref('')
const projects = ref([])
const query = ref('')

onMounted(async () => {
  try {
    const res = await fetchProjects()
    const list = Array.isArray(res) ? res : []
    // Sanitize/Normalisieren: fallback für has_build
    projects.value = list.map(p => ({
      ...p,
      // akzeptiere verschiedene mögliche API-Felder & URLs
      has_build: p?.has_build ?? p?.hasBuild ?? Boolean(p?.build_url || p?.preview_url || p?.url)
    }))
  } catch (e) {
    error.value = `Projekte konnten nicht geladen werden: ${e.message || e}`
  } finally {
    loading.value = false
  }
})

function openProject(p) {
  router.push({ name: 'ProjectViewer', params: { id: p.id } })
}

// Logout
async function goBackOrLogout() {
  try { await logout() } catch (e) { console.error("Fehler beim Logout:", e) }
  finally { router.push('/') }
}

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return projects.value
  return projects.value.filter(p =>
    [p.name, p.description, p.category, p.year, ...(p.tags || [])]
      .filter(Boolean)
      .some(t => String(t).toLowerCase().includes(q))
  )
})

function gradientStyle(p) {
  const c1 = p.color1 || '#667eea'
  const c2 = p.color2 || '#764ba2'
  return { backgroundImage: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }
}
</script>

<style scoped>
:root{
  --primary:#667eea;
  --secondary:#764ba2;
  --dark:#1a202c;
  --gray:#4a5568;
  --light:#f8fafc;
  --muted:#e2e8f0;
  --card:#ffffff;
}

/* Seite */
.portfolio-page{ background:#fff; color:var(--dark); }

/* Hero/Kopf passend zum Landing-Look */
.portfolio-hero{
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color:#fff;
  padding: 56px 0 36px;
}
.container{ max-width:1200px; margin:0 auto; padding:0 24px; }

.hero-inner{
  display:flex; align-items:flex-end; justify-content:space-between; gap:24px; flex-wrap:wrap;
}
.title{ font-size:42px; font-weight:800; line-height:1.1; }
.subtitle{ opacity:.95; margin-top:8px; }

.controls {
    display: flex;
    flex-direction: column; /* Standard: Button über Suchfeld */
    gap: 12px; /* Abstand zwischen Button und Suchfeld */
    min-width: 260px;
    /* Stellt sicher, dass die Gruppe rechts platziert wird und flexibel ist */
    margin-left: auto; 
}


.search{
  width:100%; padding:14px 16px; border-radius:12px; border:2px solid rgba(255,255,255,.4);
  background:rgba(255,255,255,.15); color:#fff; backdrop-filter: blur(8px);
}
.search::placeholder{ color:rgba(255,255,255,.85); }

/* Abschnitt */
.section{ padding: 60px 0 100px; }

/* Grid */
.grid{
  display:grid; gap:40px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}

/* Karte */
.card{
  background: var(--card);
  border-radius:20px;
  overflow:hidden;
  box-shadow: 0 4px 30px rgba(0,0,0,.08);
  transition: transform .25s ease, box-shadow .25s ease;
  cursor:pointer;
  border:1px solid #eef2f7;
}
.card:hover{
  transform: translateY(-8px);
  box-shadow: 0 12px 50px rgba(0,0,0,.12);
}
.card.disabled{ cursor:default; opacity:.85; }
.card.disabled .overlay{ display:none; }

/* Media */
.media{
  height: 220px; position:relative; display:flex; align-items:center; justify-content:center;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
}
.media img{ width:100%; height:100%; object-fit:cover; }
.device-icon{ position:absolute; opacity:.3; }
.overlay{
  position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
  background: rgba(0,0,0,.85); opacity:0; transition: opacity .25s ease;
}
.card:hover .overlay{ opacity:1; }
.view-btn{
  background:#fff; color:var(--dark); border:none; font-weight:600;
  padding:14px 22px; border-radius:12px; box-shadow:0 8px 25px rgba(255,255,255,.25);
}

/* Info */
.info{ padding:24px 24px 18px; }
.meta{ display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
.category{
  font-size:13px; font-weight:700; color:var(--primary); text-transform:uppercase; letter-spacing:.5px;
}
.year{ font-size:13px; color:var(--gray); font-weight:600; }
.name{ font-size:20px; font-weight:800; margin:6px 0 6px; color:var(--dark); }
.desc{ color:var(--gray); line-height:1.7; font-size:15px; margin-bottom:14px; }

.tags{ display:flex; gap:10px; flex-wrap:wrap; margin-bottom:6px; }
.tag{
  background:#f1f5f9; color:var(--dark); padding:6px 12px; border-radius:8px; font-size:13px; font-weight:600;
}
.badge.no-build{ font-size:12px; font-weight:700; color:#6b7280; }

/* Skeleton */
.skeleton{
  height: 360px; background:linear-gradient(90deg, #f2f4f7, #eef2f7, #f2f4f7);
  background-size:200% 100%; animation: shimmer 1.2s infinite linear; border-radius:20px;
}
@keyframes shimmer{ 0%{background-position:200% 0;} 100%{background-position:-200% 0;} }

/* Notices */
.notice{
  background:#f8fafc; border:1px solid #e2e8f0; border-radius:16px; padding:24px;
}
.notice.error{ border-color:#fecaca; background:#fff1f2; }

/* Responsive */
@media (max-width: 768px){
  .title{ font-size:32px; }
  .section{ padding: 40px 0 80px; }
  
  /* Auf Mobile: .controls-Container nimmt volle Breite ein */
  .controls {
      min-width: unset;
      width: 100%;
      order: 3; /* Sorgt dafür, dass er unter dem Titel/Subtitle steht */
      margin-top: 16px; /* Etwas Abstand vom Subtitle */
      margin-left: 0; /* Auf Mobile nicht rechtsbündig */
  }
}
</style>